import * as layout from '../../../components/markLayout.module.css';
import { MarkButton } from '../../../components/MarkControls';
import React from 'react';
import P from '@hero/ui-kit/typography/P';
import type { Step1Data } from './Step1Identity';
import type { Step2Data } from './Step2Audience';
import type { Step3Data } from './Step3Schedule';
type Props = {
    step1: Step1Data;
    step2: Step2Data;
    step3: Step3Data;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: () => void;
};
const slugPreview = (name: string): string =>
    name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'untitled';
const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className={layout.summaryRow}>
        <P noDefaultMargin strong>
            {label}
        </P>
        <P noDefaultMargin>{value}</P>
    </div>
);
const Step4Review: React.FC<Props> = ({
    step1,
    step2,
    step3,
    isSubmitting,
    submitError,
    onSubmit
}) => {
    return (
        <div className={layout.form}>
            <div className={layout.panel}>
                <Row label="Name" value={step1.name || '(empty)'} />
                <Row
                    label="Trigger type"
                    value={
                        step1.triggerType === 'SEGMENT' ? 'Segment enrollment' : 'Prospect event'
                    }
                />
                {step1.triggerType === 'PROSPECT' && (
                    <Row label="Prospect type" value={step1.prospectType} />
                )}
                <details>
                    <summary className={layout.activationSummary}>Activation configuration</summary>
                    <Row
                        label="Feature gate (predicted)"
                        value={`mark.drip.${slugPreview(step1.name)} — generated after creation`}
                    />
                </details>
            </div>
            {step1.triggerType === 'SEGMENT' && (
                <div className={layout.panel}>
                    <Row
                        label="Enrollment segment"
                        value={step2.enrollmentSegmentName ?? '(none)'}
                    />
                    <Row
                        label="Re-entry policy"
                        value={
                            step2.reEntryPolicy === 'once_ever'
                                ? 'Once per contact'
                                : step2.reEntryPolicy === 'once_per_n_days'
                                  ? `Once every ${step2.reEntryDays} days`
                                  : 'Every qualifying enrollment'
                        }
                    />
                    {step2.reEntryPolicy === 'once_per_n_days' && (
                        <Row label="Re-entry days" value={String(step2.reEntryDays)} />
                    )}
                </div>
            )}
            <div className={layout.panel}>
                <Row
                    label="Send window"
                    value={
                        step3.sendWindowStart && step3.sendWindowEnd
                            ? `${step3.sendWindowStart} – ${step3.sendWindowEnd} (recipient local time)`
                            : 'Channel defaults'
                    }
                />
            </div>
            {submitError && (
                <div role="alert" className={layout.notice}>
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-danger)'
                        }}
                    >
                        {submitError}
                    </P>
                </div>
            )}
            <MarkButton
                size="regular"
                type="button"
                onClick={onSubmit}
                disabled={isSubmitting}
                style={{
                    alignSelf: 'flex-start'
                }}
                variant="primary"
            >
                {isSubmitting ? 'Creating…' : 'Create drip'}
            </MarkButton>
        </div>
    );
};
export default Step4Review;
